import React, { useMemo } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import SessionDetailsPage from "./SessionDetails";
import LiveSessionWithDeploymentPage from "./LiveSessionWithDeployment";

function stateKey(anglerId: number, rod: number, component: string) {
  return `${anglerId}:${rod}:${component}`;
}

export default function SessionEntryPage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const wantsLive = location.pathname.startsWith("/live/");

  const sessionQuery = useQuery({
    queryKey: ["carpintel_session", id],
    queryFn: () => fetch(`/app-api/sessions/${id}`).then((r) => {
      if (!r.ok) throw new Error("Session not found");
      return r.json();
    }),
    enabled: Boolean(id),
  });
  const anglersQuery = useQuery({
    queryKey: ["carpintel_session_anglers", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/anglers`).then((r) => (r.ok ? r.json() : [])),
    enabled: Boolean(id),
  });
  const stateQuery = useQuery({
    queryKey: ["bankside_rod_state", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/rod-state`).then((r) => (r.ok ? r.json() : { states: [], positions: [] })),
    enabled: Boolean(id),
  });

  const session = sessionQuery.data;
  const anglers: any[] = anglersQuery.data ?? [];
  const states: any[] = stateQuery.data?.states ?? [];

  const setupComplete = useMemo(() => {
    if (!session || session.end_at) return true;
    if (!session.planned_end_at || !anglers.length) return false;
    const stateSet = new Set(
      states.map((row) => stateKey(Number(row.session_angler_id), Number(row.rod_number), String(row.component_type))),
    );
    return anglers.every((angler) =>
      Array.from({ length: Math.max(1, Number(angler.rods_count || 1)) }, (_, index) => index + 1).every((rod) => {
        const prefix = `${Number(angler.id)}:${rod}`;
        return (
          stateSet.has(`${prefix}:lead_system`) &&
          stateSet.has(`${prefix}:rig`) &&
          stateSet.has(`${prefix}:hookbait`)
        );
      }),
    );
  }, [session, anglers, states]);

  if (sessionQuery.isLoading || anglersQuery.isLoading || stateQuery.isLoading) {
    return <div className="mx-auto max-w-2xl px-4 py-10 text-sm text-secondary">Opening session…</div>;
  }
  if (sessionQuery.error || !session) {
    return <div className="mx-auto max-w-2xl px-4 py-10 text-sm text-secondary">That session could not be found.</div>;
  }
  if (!session.end_at && !setupComplete) {
    return <Navigate to={`/sessions/${id}/setup`} replace />;
  }
  return wantsLive ? <LiveSessionWithDeploymentPage /> : <SessionDetailsPage />;
}
